import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';

import { paytefAutoprintService, PaytefAutoprintRequest } from '@app/modules/paytef-autoprint.service';
import { UiTicketPrint } from '@app/components/model/ticket/ui-print/ui-print';

// Root-level paytef auto-print host. Lives outside the editor so when
// panel:view destroys ui-edit/ui-print mid-DoCommit, the receipt DOM
// keeps rendering long enough for ngx-qrcode to mount + decode and
// paytef to serialize a complete ticket (with PD + QR).
@Component({
    selector: 'ui-paytef-autoprint-overlay',
    templateUrl: './paytef-autoprint-overlay.html',
    styleUrls: ['./paytef-autoprint-overlay.scss']
})
export class PaytefAutoprintOverlay implements OnInit, OnDestroy {
    @ViewChild('embeddedPrint', { static: false }) embeddedPrint: UiTicketPrint;

    currentTicket: any = null;

    private _sub: Subscription = null;
    private _queue: PaytefAutoprintRequest[] = [];
    private _busy = false;

    constructor(private paytefAutoprint: paytefAutoprintService, private change: ChangeDetectorRef) {}

    ngOnInit(){
        this._sub = this.paytefAutoprint.requests$.subscribe((req) => {
            this._queue.push(req);
            this._drain();
        });
    }

    ngOnDestroy(){
        if (this._sub){
            this._sub.unsubscribe();
            this._sub = null;
        }
        this._queue = [];
    }

    private async _drain(){
        if (this._busy){
            return;     // _drain re-enters from finally / from a queue push
        }
        this._busy = true;
        try {
            while (this._queue.length > 0){
                const _req = this._queue.shift();
                try {
                    await this._process(_req);
                }
                catch(e){
                    console.error("[PAYTEF AUTOPRINT] Request failed", e);
                }
            }
        } finally {
            this._busy = false;

            // Race: si una request llega entre la salida del while
            // (queue vacio) y este finally, el subscriber habra llamado
            // a _drain() pero retorno temprano por _busy=true. Sin este
            // re-trigger la request queda colgada hasta la siguiente.
            if (this._queue.length > 0){
                Promise.resolve().then(() => this._drain());
            }
        }
    }

    private async _process(req: PaytefAutoprintRequest){
        // Mount the receipt under the embedded ui-ticket-print and
        // let CD wire up subscriptions + _TicketInfo (initial pass).
        this.currentTicket = req.ticket;
        try {
            this.change.markForCheck();
            this.change.detectChanges();
        } catch(e) {}

        // Await background VeriFactu/TicketBAI so ticket.ticketbai.qr
        // is populated before _TicketInfo (re)reads it for the print.
        // Si la promesa rechaza, el receipt va a salir SIN QR (los
        // getters de _TicketInfo lo gatean con *ngIf). Logueamos para
        // dejar trail diagnostico — si pasa repetidamente significa
        // que VeriFactu/TicketBAI esta cayendo y los tickets se estan
        // imprimiendo sin la parte fiscal requerida.
        if (req.cryptoReady){
            try {
                await req.cryptoReady;
            } catch(e) {
                console.error("[PAYTEF AUTOPRINT] cryptoReady rejected — receipt may render without VeriFactu/TicketBAI QR", e);
            }
        }

        // Esperar a que super.DoCommit confirme en backend. Si rechaza,
        // no mandamos al terminal — evita ticket fantasma en el paytef
        // cuando el commit fallo.
        if (req.commitReady){
            try {
                await req.commitReady;
            } catch(e) {
                console.warn("[PAYTEF AUTOPRINT] commit failed — skipping terminal receipt", e);
                this.currentTicket = null;
                try {
                    this.change.markForCheck();
                    this.change.detectChanges();
                } catch(e2) {}
                return;
            }
        }

        // Force a second CD pass — _TicketInfo now sees the QR and
        // flips the *ngIf gate so ngx-qrcode mounts.
        try {
            this.change.markForCheck();
            this.change.detectChanges();
        } catch(e) {}

        // Two rAFs: let the browser decode ngx-qrcode's data URL into
        // the <img> before paytef serializes the DOM.
        await new Promise<void>(_resolve => requestAnimationFrame(() => requestAnimationFrame(() => _resolve())));

        if (this.embeddedPrint){
            try {
                if (req.terminal){
                    // Paytef terminal mode
                    await this.embeddedPrint.sendToPaytefTerminal(req.terminal);
                }
                else if (req.receiptData){
                    // BAR receipt mode: el editor murio antes de poder
                    // ejecutar SendTicket con datos completos (cobro BAR
                    // sin table-view ni thumb pre-mounted). El overlay
                    // root-level sobrevive y termina el print.
                    await this.embeddedPrint.sendToBARReceipt(req.receiptData);
                }
                else {
                    console.warn("[AUTOPRINT] Ignored — neither terminal nor receiptData");
                }
            }
            catch(e){
                console.error("[AUTOPRINT] embeddedPrint method threw", e);
            }
        }
        else {
            console.warn("[AUTOPRINT] Embedded ui-ticket-print not available");
        }

        // Clear so the next request gets a fresh mount.
        this.currentTicket = null;
        try {
            this.change.markForCheck();
            this.change.detectChanges();
        } catch(e) {}
    }
}
